import React, { memo, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Box, Button, Flex, Text } from "@chakra-ui/react"

import { useCore } from "@app/hooks/useCore"

import type { ProductCombinedProps, SelectedOptionProps } from "@root/types/global"
import { ProductSizing } from "@app/components/Product/Sizing/ProductSizing"
import { SelectableOption } from "./ProductFormTypes"
import { useShopify } from "@app/hooks/useShopify"

type Props = {
  handleChange: (option: SelectedOptionProps) => void
  isLoading?: boolean
  product: ProductCombinedProps
  selectedOptions: Array<SelectedOptionProps>
  selectableOptions: SelectableOption[]
  unselectedOptions?: Array<SelectedOptionProps>
  isGiftcard?: boolean
}

export const ProductFormVariants: React.FC<Props> = memo(
  ({ handleChange, product, selectedOptions, selectableOptions, unselectedOptions, isGiftcard }) => {
    const { product: locales } = useStaticQuery<GatsbyTypes.ProductFormVariantsQuery>(graphql`
      query ProductFormVariants {
        product: sanityTemplateProduct {
          additionalSelect
          additionalSoldOut
        }
      }
    `)
    const {
      helpers: { capitalise },
    } = useCore()
    const { formatMoney } = useShopify()

    const isVariantsSamePrice = useMemo(() => {
      const basePrice = selectableOptions?.[0]?.values?.[0]?.variant?.priceV2?.amount
      return selectableOptions?.[0]?.values?.every(({ variant }) => variant?.priceV2?.amount === basePrice)
    }, [selectableOptions])

    return selectableOptions?.length > 0 ? (
      <Box color="grey.text">
        {selectableOptions.map(({ name, values }) => {
          const selectedOptionValue = selectedOptions.find(option => option.name === name)?.value
          const error = unselectedOptions?.find(option => option.name === name)
            ? [locales?.additionalSelect?.replace("{option}", capitalise(name?.replace("option", "")).toLowerCase()) || ""]
            : []
          return (
            <Box key={name}>
              <Flex mb={2} justify="space-between">
                <Text size="sm" letterSpacing="generous">{`${capitalise(name)}: ${selectedOptionValue || ""}`}</Text>
                <ProductSizing product={product} />
              </Flex>
              <Flex wrap="wrap" gap={4}>
                {values.map(({ variant, value }) => {
                  // If all variants have the same price, we don't need to display the price
                  const label =
                    isGiftcard || isVariantsSamePrice
                      ? variant?.title
                      : `${variant?.title} - ${formatMoney(parseInt(variant?.priceV2.amount))}`
                  const isAvailable = variant?.availableForSale
                  const isActive = selectedOptionValue === value
                  if (!variant?.priceV2.amount) return null
                  return (
                    <Button
                      lineHeight={1.25}
                      variant="variantSelector"
                      fontSize={14}
                      key={value}
                      isDisabled={!isAvailable}
                      isActive={isActive}
                      onClick={() => handleChange({ name, value })}
                    >
                      {label}
                    </Button>
                  )
                })}
              </Flex>
              {error &&
                error.map(e => (
                  <Text key={e} color="brand.error" mt={2} size="sm" lineHeight={1.5}>
                    {e}
                  </Text>
                ))}
            </Box>
          )
        })}
      </Box>
    ) : null
  }
)
